import React, { useState, useEffect, useRef } from "react";
import { SiteAlerts } from "../containers/alert/SiteAlerts";
import Header from "../containers/header/Header";
import Menu from "../containers/menu/Menu";
import MenuCollapsed from "../containers/menu/MenuCollapsed";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { loadRoleAccessGetDataRequest, setLoggedOut } from "../containers/login/redux/Actions";
import { loadActiveEmployeeDataRequest } from "../containers/employees/redux/Actions";
import { setTokenToHeader } from "../shared/utilites/ApiClient";
import useWindowDimensions from "../shared/components/useWindowDimensions";
import { Buffer } from "buffer";
import FloatingChatWindow from "../containers/chat/components/FloatingChatWindow";
import { usePortal } from "../contexts/PortalContext";
import useDeviceDetect from "../hooks/useDeviceDetect";

export const MainLayout = () => {
  let [sidebarToggled, setSidebarToggled] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const chatWindowRef = useRef(null);
  const { isReportMode, toggleReportMenu } = usePortal();
  const { isMobile } = useDeviceDetect();

  let wrapperClass = sidebarToggled ? " toggled" : "";
  let { width } = useWindowDimensions();

  const toggle = () => {
    setSidebarToggled(!sidebarToggled);
  };

  // Toggle function that switches based on current mode
  const handleToggle = () => {
    if (isReportMode) {
      toggleReportMenu(); // Toggle report menu in report mode
    } else {
      toggle(); // Toggle MDS menu in MDS mode
    }
  };

  let { isLoggedIn, roleAccess } = useSelector((state) => state.login);
  let { active_employees } = useSelector((state) => state.employee);
  const [userLoggedIn, setUserLoggedIn] = useState(isLoggedIn);
  const [currentUser, setCurrentUser] = useState(null);

  useEffect(() => {
    if (!!localStorage.getItem("employee")) {
      let empInfo = JSON.parse(localStorage.getItem('employee'));
      dispatch(loadRoleAccessGetDataRequest(empInfo.emp_no));

      // Only fetch active employees if not already loaded
      if (!active_employees || active_employees.length === 0) {
        dispatch(loadActiveEmployeeDataRequest());
      }

      setTokenToHeader(empInfo.token);
      setCurrentUser(empInfo);
    }
  }, [dispatch, active_employees]);

  const checkForExpiration = () => {
    try {
      let jwt_token = localStorage.getItem('jwt_token');
      if (jwt_token) {
        let parsed_jwt = Buffer.from(jwt_token.split('.')[1], 'base64').toString();
        let jwt_object = JSON.parse(parsed_jwt);
        let jwt_exp = jwt_object.exp;

        if (new Date() > new Date(jwt_exp * 1000)) {
          setUserLoggedIn(false);
        }
      } else {
        setUserLoggedIn(false);
      }
    } catch(e) {
      setUserLoggedIn(false);
    }
  };

  useEffect(() => {
    checkForExpiration();
    if (width <= 768) {
      setSidebarToggled(true);
    }
  }, [location.pathname]);

  useEffect(() => {
    if (!userLoggedIn) {
      // Store the current path for return after login
      // Exclude login/logout/reset pages
      const excludedPaths = ['/login', '/logout', '/reset', '/admin-portal/login', '/powerbi-portal/login'];
      const shouldStoreReturn = !excludedPaths.some(path => location.pathname.startsWith(path));
      
      if (shouldStoreReturn && location.pathname !== '/') {
        sessionStorage.setItem('returnUrl', location.pathname);
      }

      // Don't redirect if we're already on a login page (including logout which redirects to login)
      const isOnLoginPage = excludedPaths.some(path => location.pathname.startsWith(path));

      if (!isOnLoginPage) {
        // Determine which portal the user was in before session expired
        const portalType = localStorage.getItem('portal_type');
        const isInReportPortal = location.pathname.includes('/powerbi-portal') || isReportMode || portalType === 'report';
        const loginPath = isInReportPortal ? '/powerbi-portal/login' : '/login';

        localStorage.removeItem('jwt_token');
        localStorage.removeItem('jwt_time');
        localStorage.removeItem('employee');
        dispatch(setLoggedOut());
        navigate(loginPath, { replace: true });
      }
    }
  }, [userLoggedIn, roleAccess, location.pathname, navigate, dispatch, isReportMode]);

  return (
    <div className={"wrapper " + wrapperClass}>
      {/* Show MDS menu in MDS mode */}
      {!isReportMode && (
        <>
          {(sidebarToggled && width > 768) ? (
            <div>
              <MenuCollapsed />
            </div>
          ) : (
            <div className="menu">
              <Menu toggled={sidebarToggled} toggleMenu={toggle} />
            </div>
          )}
        </>
      )}

      {/* Note: ReportsMenu is now rendered within PowerBILayout component */}
      {/* Removed duplicate menu rendering to fix double menu display issue */}

      <div className="main" style={{ marginLeft: isReportMode ? '0' : undefined }}>
        {(!isReportMode || isMobile) && <Header toggleMenu={handleToggle} />}
        {userLoggedIn && <Outlet />}
      </div>

      {/* Floating Chat Bubble - Available only in MDS mode */}
      {userLoggedIn && currentUser && !isReportMode && (
        <FloatingChatWindow
          ref={chatWindowRef}
          currentUser={currentUser}
          allUsers={active_employees || []}
        />
      )}
    </div>
  );
};

export default MainLayout;